import { cancelFeeTypeForNonZeroYenReservationKey } from "@/souhl_admin/components/features/companies/sms-sending-rule/companySmsSendingRuleV2/CompanySmsSendingRuleV2.const"
import type { CancelType } from "@/souhl_admin/gql/__generated__/graphql"
import { CompanySmsSendingRuleV2EditCancelTypeSetting } from "@/souhl_admin/utils/form/companySmsSendingRuleV2Edit"

// Backend-aligned initial rows for create mode
export const buildDefaultCancelTypeSettingInputs =
	(): CompanySmsSendingRuleV2EditCancelTypeSetting[] => [
		{
			id: "",
			cancelType: "NO_CONFIRMATION" as CancelType,
			cancelFeeTypeForNonZeroYenReservation:
				cancelFeeTypeForNonZeroYenReservationKey.PERCENT,
			cancelFeePercentForNonZeroYenReservation: 100,
			cancelFeeAmountForNonZeroYenReservation: undefined,
			cancelFeeAmountForZeroYenReservation: 10000,
			minVisitCountForClaimingConversion: 1,
			minVisitCountForManuallyClaimingCancelFee: 20,
			minVisitCountForEncouragingRereservation: 20,
			minVisitCountForNothing: 20,
			claimingConversionPolicyName: "請求転換 5日後",
			encouragingRereservationPolicyName: null,
			encouragingRereservationUnits: [],
			manuallyClaimingUnits: [],
		},
		{
			id: "",
			cancelType: "ON_THE_DAY" as CancelType,
			cancelFeeTypeForNonZeroYenReservation:
				cancelFeeTypeForNonZeroYenReservationKey.PERCENT,
			cancelFeePercentForNonZeroYenReservation: 80,
			cancelFeeAmountForNonZeroYenReservation: undefined,
			cancelFeeAmountForZeroYenReservation: 8000,
			minVisitCountForClaimingConversion: 0,
			minVisitCountForManuallyClaimingCancelFee: 20,
			minVisitCountForEncouragingRereservation: 20,
			minVisitCountForNothing: 20,
			claimingConversionPolicyName: "請求転換 5日後",
			encouragingRereservationPolicyName: null,
			encouragingRereservationUnits: [],
			manuallyClaimingUnits: [],
		},
		{
			id: "",
			cancelType: "PREVIOUS_DAY" as CancelType,
			cancelFeeTypeForNonZeroYenReservation:
				cancelFeeTypeForNonZeroYenReservationKey.PERCENT,
			cancelFeePercentForNonZeroYenReservation: 50,
			cancelFeeAmountForNonZeroYenReservation: undefined,
			cancelFeeAmountForZeroYenReservation: 5000,
			minVisitCountForClaimingConversion: 0,
			minVisitCountForManuallyClaimingCancelFee: 0,
			minVisitCountForEncouragingRereservation: 20,
			minVisitCountForNothing: 20,
			claimingConversionPolicyName: null,
			encouragingRereservationPolicyName: null,
			encouragingRereservationUnits: [],
			manuallyClaimingUnits: [],
		},
		{
			id: "",
			cancelType: "MORE_THAN_TWO_DAYS_AGO" as CancelType,
			cancelFeeTypeForNonZeroYenReservation:
				cancelFeeTypeForNonZeroYenReservationKey.PERCENT,
			cancelFeePercentForNonZeroYenReservation: 0,
			cancelFeeAmountForNonZeroYenReservation: undefined,
			cancelFeeAmountForZeroYenReservation: 0,
			minVisitCountForClaimingConversion: 0,
			minVisitCountForManuallyClaimingCancelFee: 0,
			minVisitCountForEncouragingRereservation: 0,
			minVisitCountForNothing: 20,
			claimingConversionPolicyName: null,
			encouragingRereservationPolicyName: "キャンセル発生 翌日",
			encouragingRereservationUnits: [],
			manuallyClaimingUnits: [],
		},
	]

// ZERO_THREE 用のデフォルト行（6 cancel types）
const zeroThreeBase: Omit<
	CompanySmsSendingRuleV2EditCancelTypeSetting,
	"cancelType"
> = {
	id: "",
	cancelFeeTypeForNonZeroYenReservation:
		cancelFeeTypeForNonZeroYenReservationKey.PERCENT,
	cancelFeePercentForNonZeroYenReservation: 0,
	cancelFeeAmountForNonZeroYenReservation: undefined,
	cancelFeeAmountForZeroYenReservation: 0,
	minVisitCountForClaimingConversion: 0,
	minVisitCountForManuallyClaimingCancelFee: 0,
	minVisitCountForEncouragingRereservation: 0,
	minVisitCountForNothing: 0,
	claimingConversionPolicyName: null,
	encouragingRereservationPolicyName: null,
	encouragingRereservationUnits: [],
	manuallyClaimingUnits: [],
}

const ZERO_THREE_CANCEL_TYPES: CancelType[] = [
	"NO_CONFIRMATION",
	"ON_THE_DAY",
	"PREVIOUS_DAY",
	"TWO_DAYS_AGO",
	"THREE_DAYS_AGO",
	"MORE_THAN_FOUR_DAYS_AGO",
]

export const buildDefaultCancelTypeSettingInputsForZeroThree =
	(): CompanySmsSendingRuleV2EditCancelTypeSetting[] =>
		ZERO_THREE_CANCEL_TYPES.map((cancelType) => ({
			...zeroThreeBase,
			cancelType,
		}))

// ZERO_FIVE 用のデフォルト行（8 cancel types）
// backend の acceptable_cancel_types_for_zero_five_cancel_policy_template に対応する。
// ベースは zero_three と同じ非請求ベースを流用し、金額は作成後に編集する想定。
const ZERO_FIVE_CANCEL_TYPES: CancelType[] = [
	"NO_CONFIRMATION",
	"ON_THE_DAY",
	"PREVIOUS_DAY",
	"TWO_DAYS_AGO",
	"THREE_DAYS_AGO",
	"FOUR_DAYS_AGO",
	"FIVE_DAYS_AGO",
	"MORE_THAN_SIX_DAYS_AGO",
]

export const buildDefaultCancelTypeSettingInputsForZeroFive =
	(): CompanySmsSendingRuleV2EditCancelTypeSetting[] =>
		ZERO_FIVE_CANCEL_TYPES.map((cancelType) => ({
			...zeroThreeBase,
			cancelType,
		}))

// UENO_CLINIC 用のデフォルト行（9 cancel types）
// 上野クリニックは全て非請求で再予約案内のため、zero_three と同じ非請求ベースを流用する
// backend の acceptable_cancel_types_for_UENO_CLINIC_cancel_policy_template（未契約理由 5 種 +
// 確定後キャンセル 4 種）と揃えること。ズレると rule_v2 作成時にバリデーションで弾かれる
const UENO_CLINIC_CANCEL_TYPES: CancelType[] = [
	"UENO_CLINIC_PRICE",
	"UENO_CLINIC_SCHEDULE",
	"UENO_CLINIC_FEAR",
	"UENO_CLINIC_FAMILY_CONSULTATION",
	"UENO_CLINIC_OTHER",
	"UENO_CLINIC_CONFIRMED_CANCEL_PRICE",
	"UENO_CLINIC_CONFIRMED_CANCEL_SCHEDULE",
	"UENO_CLINIC_CONFIRMED_CANCEL_FEAR",
	"UENO_CLINIC_CONFIRMED_CANCEL_FAMILY_CONSULTATION",
]

export const buildDefaultCancelTypeSettingInputsForUenoClinic =
	(): CompanySmsSendingRuleV2EditCancelTypeSetting[] =>
		UENO_CLINIC_CANCEL_TYPES.map((cancelType) => ({
			...zeroThreeBase,
			cancelType,
		}))
